import { PaginationConfig } from "ant-design-vue/types/list/list"
import { computed, Ref, ref, watch } from "vue"

import i18n from "@/i18n"

type PaginationPosition = "top" | "bottom" | "both"

type PaginationFilters = Record<string, string[] | null>

type PaginationSorter = {
  columnKey: string
  field: string
  order: "ascend" | "descend" | null
}

export type FetcherParams = {
  page: number
  pageSize: number
  ordering?: string
  [key: string]: any
}

type PaginationData = {
  key: string
  position?: PaginationPosition
  total: Ref<number>
  fetcher?: (params: FetcherParams) => unknown
}

export const useAntTable = (data: PaginationData) => {
  const current = ref(1)
  const pageSize = ref(10)

  const getPageSize = () => {
    let size = 10

    if (data.key) {
      const value = localStorage.getItem(`tablePageSize-${data.key}`)

      if (value) {
        size = +value
      }
    }

    return size
  }

  // Initialize pageSize
  pageSize.value = getPageSize()

  watch(pageSize, (newSize) => {
    if (data.key) {
      localStorage.setItem(`tablePageSize-${data.key}`, newSize.toString())
    }
  })

  const savedFilters = ref<PaginationFilters>({})
  const savedSorter = ref<PaginationSorter | null>(null)

  const makeFetcherParams = () => {
    // потрібно зробити так щоб фільтри були у форматі key: string, тому що з таблиці приходять у форматі key: string[] | null
    const normalizedFilters: Record<string, string> = {}

    if (savedFilters.value) {
      Object.entries(savedFilters.value).forEach(([key, value]) => {
        if (value && Array.isArray(value) && value.length > 0) {
          normalizedFilters[key] = value.join(",")
        }
      })
    }

    const params: FetcherParams = {
      page: current.value,
      pageSize: pageSize.value,
      filters: normalizedFilters
    }

    if (savedSorter.value && savedSorter.value.field && savedSorter.value.order) {
      params.ordering =
        savedSorter.value.order === "ascend"
          ? savedSorter.value.field
          : `-${savedSorter.value.field}`
    }

    return params
  }

  const refreshTable = () => {
    if (data.fetcher) {
      return data.fetcher(makeFetcherParams())
    }
  }

  const onTableChange = (
    pagination: PaginationConfig,
    filters: PaginationFilters,
    sorter: PaginationSorter
  ) => {
    current.value = pagination.current || 1
    pageSize.value = pagination.pageSize || 10
    savedFilters.value = filters
    savedSorter.value = sorter

    refreshTable()
  }

  const resetTableFilters = () => {
    savedFilters.value = {}
    savedSorter.value = null
    current.value = 1
    refreshTable()
  }

  const hasTableFilters = computed(() => {
    return Object.values(savedFilters.value).some(
      (value) => value && Array.isArray(value) && value.length > 0
    )
  })

  const tablePagination = computed(() => {
    return {
      current: current.value,
      pageSize: pageSize.value,
      showSizeChanger: true,
      position: data.position || "bottom",
      pageSizeOptions: ["10", "20", "50", "100"],
      total: data.total.value,
      showTotal: (total: number, range: [number, number]) =>
        `${range[0]}-${range[1]} ${i18n.t("pagination.of")} ${total} ${i18n.t("pagination.items")}`
    }
  })

  return {
    current,
    pageSize,
    tablePagination,
    hasTableFilters,
    onTableChange,
    refreshTable,
    resetTableFilters
  }
}
